import { useMemo } from "react";
import { useSearchParams } from "react-router";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER, formatShortDate } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { DutyScheduleDisplayStatus, DutyScheduleResponse, dutySchedulesApi } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { formatUserCode } from "@/common/utils/formatUserCode";
import { formatChangedAt } from "@/pages/duty-schedule/DutySchedule.utils";
import { DutyScheduleRowMenu } from "@/pages/duty-schedule/components/row-menu/DutyScheduleRowMenu";
import { DutyScheduleStatusChip } from "@/pages/duty-schedule/components/status-chip/DutyScheduleStatusChip";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

const SHOW_PAST_PARAM = "showPast";
const ACTIONS_COLUMN_WIDTH = 64;

interface Options {
  onRelease: (dutySchedule: DutyScheduleResponse) => void;
  onDelete: (dutySchedule: DutyScheduleResponse) => void;
  isBusy: boolean;
}

export function useDutySchedules({ onRelease, onDelete, isBusy }: Options) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const showPast = searchParams.get(SHOW_PAST_PARAM) === "true";

  const { data, isLoading } = useQuery({
    queryKey: [QueryKey.DutySchedules, showPast],
    queryFn: () => dutySchedulesApi.getAllDutySchedules({ includePast: showPast }),
    meta: { loadingScope: "pageContent" },
  });

  const columns = useMemo<Array<GridColDef<DutyScheduleResponse>>>(
    () => [
      {
        field: "name",
        headerName: t("dutySchedule.columns.name"),
        flex: 2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={params.row.name} variant="body2" fontWeight={700} />
        ),
      },
      {
        field: "periodStart",
        headerName: t("dutySchedule.columns.periodStart"),
        type: "date",
        flex: 1,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={formatShortDate(params.row.periodStart, locales[language])} variant="body2" />
        ),
      },
      {
        field: "periodEnd",
        headerName: t("dutySchedule.columns.periodEnd"),
        type: "date",
        flex: 1,
        minWidth: 120,
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={formatShortDate(params.row.periodEnd, locales[language])} variant="body2" />
        ),
      },
      {
        field: "status",
        headerName: t("dutySchedule.columns.status"),
        flex: 1,
        minWidth: 140,
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <DutyScheduleStatusChip status={params.row.status} />
        ),
      },
      // The three key figures are calculated by the detail page and stay empty until then.
      {
        field: "workingDays",
        headerName: t("dutySchedule.columns.workingDays"),
        flex: 1,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={params.row.workingDays?.toString() ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
        ),
      },
      {
        field: "dutyCount",
        headerName: t("dutySchedule.columns.dutyCount"),
        flex: 1,
        minWidth: 100,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={params.row.dutyCount?.toString() ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
        ),
      },
      {
        field: "counselorCount",
        headerName: t("dutySchedule.columns.counselorCount"),
        flex: 1,
        minWidth: 200,
        align: "right",
        headerAlign: "right",
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText value={params.row.counselorCount?.toString() ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
        ),
      },
      {
        field: "lastModifiedAtUtc",
        headerName: t("dutySchedule.columns.lastModified"),
        type: "date",
        flex: 2,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) => (
          <EllipsisText
            value={t("dutySchedule.changedBy", {
              date: formatChangedAt(params.row.lastModifiedAtUtc, language),
              user: formatUserCode(params.row.lastModifiedByUserCode, t),
            })}
            variant="body2"
          />
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: ACTIONS_COLUMN_WIDTH,
        sortable: false,
        filterable: false,
        align: "right",
        // A released schedule is write-protected, so it offers no actions.
        renderCell: (params: GridRenderCellParams<DutyScheduleResponse>) =>
          params.row.status === DutyScheduleDisplayStatus.InProgress ? (
            <DutyScheduleRowMenu
              name={params.row.name}
              onRelease={() => onRelease(params.row)}
              onDelete={() => onDelete(params.row)}
              isBusy={isBusy}
            />
          ) : null,
      },
    ],
    [t, language, onRelease, onDelete, isBusy]
  );

  function setShowPast(value: boolean) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(SHOW_PAST_PARAM, "true");
      } else {
        next.delete(SHOW_PAST_PARAM);
      }
      return next;
    });
  }

  return {
    dutySchedules: data ?? [],
    isLoading,
    columns,
    showPast,
    setShowPast,
  };
}
